import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import type { ScanCadenceBase } from '@/types';

/**
 * Reads/writes the per-tenant base scan cadence (`scan_cadence_base` setting).
 *
 * Mirrors `useScanSchedule`'s caching (30s stale / 5min gc) — NOT
 * `useScanLevel`'s 0/0 (which causes refetch loops; FIDELITY G5). It reads the
 * same `useMy('setting')` cache the schedule hook uses, so one network read
 * serves both (TanStack dedupes the identical key). Impersonation-safe via
 * `email` in the query key (handled by `useMy` / `useGetUserKey`).
 *
 * Writes are `silent: true` because the wizard emits ONE combined toast for the
 * two-setting save (OPEN-Q-3). `useUpdateSetting` invalidates
 * `['my','setting',email]` automatically.
 *
 * The hook NEVER writes `anchorDate` — the `ScanCadenceBase` type omits it and
 * the backend stamps it on save (G2).
 */

const DEFAULT_CADENCE: ScanCadenceBase = {
  frequency: 'weekly',
  dayOfWeek: 0,
  dayOfMonth: 1,
  time: '00:00',
};

export function useScanCadence({ email }: { email?: string } = {}) {
  const {
    data: settings,
    isPending,
    status,
    refetch,
  } = useMy(
    { resource: 'setting' },
    {
      email,
      staleTime: 30000, // 30 seconds
      gcTime: 300000, // 5 minutes
    }
  );

  const { mutateAsync: updateSetting, isPending: isUpdating } =
    useUpdateSetting(email);

  const raw = settings?.find(s => s.name === 'scan_cadence_base')?.value as
    | ScanCadenceBase
    | undefined;
  const isSet = raw !== undefined;
  const currentCadence: ScanCadenceBase = raw ?? DEFAULT_CADENCE;

  const updateScanCadenceAsync = (value: ScanCadenceBase) =>
    updateSetting({ name: 'scan_cadence_base', value, silent: true });

  return {
    currentCadence,
    isSet,
    isLoadingCadence: isPending,
    status,
    updateScanCadenceAsync,
    isUpdatingCadence: isUpdating,
    refetch,
  };
}
